<script setup lang="ts">
import {nextTick, onMounted, onUnmounted, ref, useTemplateRef} from "vue";

interface TableRow {
  band: string;
  redressAwarded: string;
  maxPercentage: string;
  maxTotal: string;
}

const tableData: TableRow[] = [
  {band: '1', redressAwarded: '1-1,499', maxPercentage: '30%', maxTotal: '420'},
  {band: '2', redressAwarded: '1,500 - 9,999', maxPercentage: '28%', maxTotal: '2,500'},
  {band: '3', redressAwarded: '10,000 - 24,999', maxPercentage: '25%', maxTotal: '5,000'},
  {band: '4', redressAwarded: '25,000 - 49,999', maxPercentage: '20%', maxTotal: '7,500'},
  {band: '5', redressAwarded: '50,000 or above', maxPercentage: '15%', maxTotal: '10,000'},
];

const isMobile = ref(false);
const wrapper = useTemplateRef<HTMLDivElement>('wrapper')

const thBaseClass = 'border border-gray-200 px-4 py-2 text-left font-bold'
const tdBaseClass = 'border border-gray-300 px-4 py-2'
const tdSpanBaseClass = 'font-bold w-1/2 mr-1'

const checkIsMobile = () => {
  isMobile.value = (wrapper.value?.clientWidth ?? 0) < 400; // Tailwind's 'md' breakpoint is 768px
};

onMounted(async () => {
  await nextTick();

  checkIsMobile();
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', checkIsMobile);
  }
});

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', checkIsMobile);
  }
});
</script>

<template>
  <div ref="wrapper">
    <p class="mb-4">PCP Financial Mis-selling cases pursued by Johnson Law Group are protected by
      the
      SRA fee cap
      where a statutory redress scheme applies (such as a claim via the Financial Ombudsman Service
      or
      a statutory redress scheme set up by the Financial Conduct Authority), the amount a legal
      advisor can charge you for their services is capped by the SRA Regulatory Arrangements (Claims
      Management Fees) Rules 2024.</p>
    <p class="mb-4">For the work we do for you, the amount we can charge you will be dependent upon
      the size of the
      compensation you recover. It is the lower of either a percentage of your damages or the
      maximum
      total charge as set out in the table below. These figures include all associated expenses we
      incur with your claim but do not include VAT.</p>

    <table class="w-full border border-gray-300 mb-4">
      <thead>
      <tr :class="{ hidden: isMobile, 'table-row bg-gray-200': !isMobile}">
        <th :class="thBaseClass">Band</th>
        <th :class="thBaseClass">Redress awarded for a claim
          (£)
        </th>
        <th :class="thBaseClass">The maximum percentage rate
          of charge
        </th>
        <th :class="thBaseClass">The maximum total charge
          (£)
        </th>
      </tr>
      </thead>
      <tbody
        :class="{ 'table-row-group': !isMobile}"
        class="[&>*]:odd:bg-white  [&>*]:even:bg-gray-100 [&>*]:[&>*]:border [&>*]:[&>*]:border-gray-200">
      <tr
        v-for="(row, index) in tableData"
        :key="index"
        :class="{
          'block cursor-pointer': isMobile,
          'table-row': !isMobile
        }"
      >
        <td :class="[tdBaseClass, isMobile ? 'block' : 'table-cell']">
          <span :class="[tdSpanBaseClass, isMobile ? 'inline-block' : 'hidden' ]">Band:</span>
          {{ row.band }}
        </td>
        <td :class="[tdBaseClass, isMobile ? 'block' : 'table-cell']">
          <span :class="[tdSpanBaseClass, isMobile ? 'inline-block' : 'hidden' ]">Redress awarded for a claim (£):</span>
          {{ row.redressAwarded }}
        </td>
        <td :class="[tdBaseClass, isMobile ? 'block' : 'table-cell']">
          <span :class="[tdSpanBaseClass, isMobile ? 'inline-block' : 'hidden' ]">The maximum percentage rate of charge:</span>
          {{ row.maxPercentage }}
        </td>
        <td :class="[tdBaseClass, isMobile ? 'block' : 'table-cell']">
          <span :class="[tdSpanBaseClass, isMobile ? 'inline-block' : 'hidden' ]">The maximum total charge (£):</span>
          {{ row.maxTotal }}
        </td>
      </tr>
      </tbody>
    </table>

    <p class="font-bold mb-4">Example</p>
    <ul class="list-disc px-4 mb-4">
      <li>If you recover £1,000 in damages, we will charge you £300 plus VAT (30% of your damages).
        Total charge = £360, so you will receive £640.
      </li>
      <li>If you recover £1,499 in damages, the 30% charge would take us to £449.70, which is above
        the maximum £420 we can charge in that banding. Our charges will therefore be restricted to
        £420 plus VAT (£504). You will get £995 in compensation after deduction of our charges.
      </li>
    </ul>
    <p class="mb-4">We obviously cannot predict exactly how much we will charge until you are
      awarded
      compensation.</p>
    <p class="mb-4">It’s your decision. You should also be aware that, because your claim is being
      pursued this
      way, you are able to bring the claim yourself free of charge and without representation if you
      so wish.</p>
    <p class="mb-4">We cannot advise you to use and pay for our services over doing it yourself.
      Some
      people prefer
      to seek the assistance of a legal professional, so they don’t have to do the work, but others
      prefer to do it themselves. There is no right or wrong way to do this.</p>
    <p class="mb-4">If for any reason we later advise you to pursue your claim by other means where
      the fee cap no
      longer applies (such as through the Court), we confirm that we will not take any further
      action
      on your behalf until all additional costs consequences are fully explained to you and you
      confirm you wish to proceed on that basis.</p>
    <p>
      Finally, when we send you a bill for our services at the end of your claim, we will ensure
      that
      any charges that fall outside the fee cap are specified on any invoice we send you.
    </p>
  </div>
</template>

